import React, { useCallback, useEffect } from 'react'
import { Typography, Space, Button, Card, Tag } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { StepComponentProps, OnboardingV2Data } from '../index'
import VTLogoPurple from '../../../assets/images/VT-Logo-Purple.svg'
import IntlMessages from '../../../util/IntlMessages'
import { useOnboardingContext } from '../context'
import { track } from '../../../services/analytics/analytics'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'
 
const { Title, Paragraph, Text } = Typography

export const WelcomeStep: React.FC<StepComponentProps<OnboardingV2Data>> = ({
  onNext,
  onExploreFirst,
}) => {
  const { dispatch: updateData, formData: data } = useOnboardingContext()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)

  // Screen shown
  useEffect(() => {
    if (!authCompany || !data?.authUser) return
    track('ONBOARDING_WELCOME_SCREEN_SHOWN', {
      company_id: authCompany.id,
      distinct_id: data.authUser.id,
      email: data.authUser.email,
      plan: authCompany.plan,
      platform: authCompany.platform,
    })
  }, [authCompany, data?.authUser])

  const handleStartSetup = useCallback(async () => {
    updateData({ type: 'UPDATE_FORM_DATA', payload: { hasSeenWelcome: true } })
    if (authCompany && data?.authUser) {
      track('ONBOARDING_START_SETUP_CLICKED', {
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
    }
    if (onNext) {
      await onNext()
    }
  }, [updateData, onNext, authCompany, data?.authUser])

  const handleExploreFirst = useCallback(() => {
    updateData({ type: 'UPDATE_FORM_DATA', payload: { hasSeenWelcome: true } })
    // If onExploreFirst is provided, use it; otherwise, just close the modal
    if (authCompany && data?.authUser) {
      track('ONBOARDING_WELCOME_SCREEN_DISMISSED', {
        dismiss_method: 'EXPLORE_FIRST',
        company_id: authCompany.id,
        user_id: data.authUser.id,
      })
    }
    if (onExploreFirst) {
      onExploreFirst()
    }
  }, [updateData, onExploreFirst, authCompany, data?.authUser])

  const currentYear = new Date().getFullYear()
  const holidaysCurrentYear = (data.holidays || []).filter((holiday) => {
    if (!holiday?.date) return false
    const yearString = String(holiday.date).slice(0, 4)
    const year = Number(yearString)
    return year === currentYear
  })
  const visibleHolidays = holidaysCurrentYear.slice(0, 3)
  const moreCount = Math.max(holidaysCurrentYear.length - 3, 0)

  // Users and approvers (now include imageUrl in getCompanyAndUserInfo)
  const teamUsers = data.authUser?.team?.users || []

  return (
    <div className="welcome-step">
      <div className="welcome-step-content">
        <div className="welcome-left-section">
          <VTLogoPurple style={{ width: '240px', height: 'auto' }} />
          <div className="welcome-title-container">
            <Title level={2} className="welcome-title-line">
              <IntlMessages id="onboarding.welcome.title" />
            </Title>
            <Title level={2} className="welcome-title-line">
              <IntlMessages id="onboarding.welcome.subtitle" />
            </Title>
            <Title level={2} className="welcome-title-line welcome-plan">
              {
                authCompany?.plan === SubscriptionPlanEnum.freemium
                  ? <IntlMessages id="app.freeplan" />
                  : authCompany?.plan || 'Complete'
              }
            </Title>
          </div>

          <Paragraph className="welcome-intro">
            <IntlMessages id="onboarding.welcome.intro" values={{ companyName: (<span> {authCompany?.name || 'your company'} </span>) }}/>
          </Paragraph>

          <div className="setup-checklist">
            <Space direction="vertical" size="middle">
              <div className="checklist-item">
                <CheckOutlined className="checklist-icon" />
                <Text>{
                  authCompany?.plan === SubscriptionPlanEnum.freemium
                    ? <IntlMessages id="onboarding.welcome.checklist.freemium.setupLocation" />
                    : <IntlMessages id="onboarding.welcome.checklist.setupLocation" />
                }</Text>
              </div>
              <div className="checklist-item">
                <CheckOutlined className="checklist-icon" />
                <Text><IntlMessages id="onboarding.welcome.checklist.addDepartment" /></Text>
              </div>
              <div className="checklist-item">
                <CheckOutlined className="checklist-icon" />
                <Text><IntlMessages id="onboarding.welcome.checklist.startManaging" /></Text>
              </div>
            </Space>
          </div>

          <div className="welcome-actions">
            <Button
              type="primary"
              size="large"
              className="start-setup-btn"
              onClick={() => { void handleStartSetup() }}
            >
              <IntlMessages id="onboarding.welcome.actions.startSetup" />
            </Button>

            <Button
              type="link"
              onClick={handleExploreFirst}
              className="explore-first-link"
            >
              <IntlMessages id="onboarding.welcome.actions.exploreFirst" />
            </Button>
          </div>
        </div>

        <div className="welcome-right-section">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card className="location-info-card">
              <Card.Meta
                title={
                  <IntlMessages
                    id="onboarding.welcome.locationInfo.title"
                    values={{
                      locationName: data.authUser?.location?.name || data.locationName || 'No location set',
                    }}
                  />
                }
                description={
                  <div className="location-details">
                    <div className="subsection">
                      <Text strong className="subsection-title"><IntlMessages id="onboarding.welcome.locationInfo.leavePolicies" /></Text>
                      <div className="subsection-content leave-policies-content">
                        {data.authUser?.location?.leavePolicies && data.authUser.location.leavePolicies.length > 0 ? (
                          data.authUser.location.leavePolicies.map((policy, index) => (
                            <Tag
                              key={index}
                              color={policy.leaveType?.color || '#f0f0f0'}
                              className="policy-pill"
                            >
                              {policy.leaveType?.name || 'Unknown leave type'}
                            </Tag>
                          ))
                        ) : (
                          <Text type="secondary"><IntlMessages id="onboarding.welcome.locationInfo.noPolicies" /></Text>
                        )}
                      </div>
                    </div>

                    <div className="subsection">
                      <Text strong className="subsection-title"><IntlMessages id="onboarding.welcome.locationInfo.holidays" /></Text>
                      <div className="subsection-content">
                        {holidaysCurrentYear.length > 0 ? (
                          visibleHolidays.map((holiday, index) => (
                            <Text key={index} className="holiday-item">
                              • {holiday.name}
                            </Text>
                          ))
                        ) : (
                          <Text type="secondary"><IntlMessages id="onboarding.welcome.locationInfo.noHolidays" /></Text>
                        )}
                        {holidaysCurrentYear.length > 3 && (
                          <Text type="secondary" className="more-items">
                            <IntlMessages id="onboarding.welcome.locationInfo.moreItems" values={{ count: moreCount }} />
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                }
              />
            </Card>

            <Card className="team-info-card">
              <Card.Meta
                title={<IntlMessages
                  id="onboarding.welcome.teamInfo.title"
                  values={{
                    departmentName:
                      data.authUser?.team?.name ||
                      data.departments?.[0]?.name ||
                      'No department set',
                  }}
                />
                }
                description={
                  <div className="team-details">
                    <div className="subsection">
                      <Text strong className="subsection-title"><IntlMessages id="onboarding.welcome.teamInfo.departments" /></Text>
                      <div className="subsection-content departments-content">
                        {data.departments && data.departments.length > 0 ? (
                          data.departments.map((department, index) => (
                            <Tag
                              key={index}
                              color="#1890ff"
                              className="department-pill"
                            >
                              {department.name}
                            </Tag>
                          ))
                        ) : (
                          <Text type="secondary"><IntlMessages id="onboarding.welcome.teamInfo.noDepartments" /></Text>
                        )}
                      </div>
                    </div>

                    <div className="subsection">
                      <Text strong className="subsection-title">
                        <IntlMessages id="onboarding.welcome.teamInfo.users" /></Text>
                      <div className="subsection-content">
                        {teamUsers.length === 0 && data.authUser ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <UserAvatar
                              id={data.authUser.id}
                              name={data.authUser.name}
                              avatar={data.authUser.imageUrl}
                              avatarSize="small"
                              isShowingName={false}
                              isLinkToPage={false}
                              isTooltip={true}
                              shape="circle"
                            />
                          </div>
                        ) : teamUsers.length > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {teamUsers.slice(0, 4).map((user) => (
                              <span className="user-avatar" key={user.id}>
                                <UserAvatar
                                  id={user.id}
                                  name={user.name}
                                  avatar={user.imageUrl}
                                  avatarSize="small"
                                  isShowingName={false}
                                  isLinkToPage={false}
                                  isTooltip={true}
                                  shape="circle"
                                />
                              </span>
                            ))}
                            {teamUsers.length > 4 && (
                              <span className="onb-inline-count">+{teamUsers.length - 4}</span>
                            )}
                          </div>
                        ) : (
                          <Text type="secondary"><IntlMessages id="onboarding.welcome.teamInfo.noUsers" /></Text>
                        )}
                      </div>
                    </div>
                  </div>
                }
              />
            </Card>
          </Space>
        </div>
      </div>
    </div>
  )
}
